import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Star, 
  Users, 
  Target, 
  TrendingUp, 
  MapPin, 
  Dumbbell,
  Heart,
  BarChart3,
  Layers,
  ArrowRight,
  CheckCircle,
  Calendar,
  Clock
} from 'lucide-react';

const BOOKING_URL = "https://link.sweatsetter.com/widget/booking/0bvqp14e0y6pNvAdcqsM";

const programs = [
  { icon: Users, title: 'Large Group Training', desc: 'High-energy coach-led workouts that build strength, endurance, and accountability.' },
  { icon: Layers, title: 'Semi-Private Training', desc: 'More personalized coaching in a small-group setting.' },
  { icon: Target, title: 'Personal Training', desc: 'One-on-one training built around your body, goals, and schedule.' },
  { icon: Heart, title: 'Nutrition Coaching', desc: 'Support and structure to help your effort in the gym match your results.' },
  { icon: Dumbbell, title: 'HYROX Training', desc: 'Train with an official HYROX affiliate for race-day confidence.' },
  { icon: BarChart3, title: 'Assessments', desc: 'Get real data on your body composition and performance over time.' },
];

const differentiators = [
  { icon: Target, title: 'Coaching, Not Guesswork', desc: 'Every workout is led by experienced coaches who help you train safely and effectively.' },
  { icon: MapPin, title: 'Indoor and Outdoor Variety', desc: 'Train in our Scott\'s Addition facility and at some of Richmond\'s best outdoor locations.' },
  { icon: Users, title: 'A Community That Keeps You Accountable', desc: 'Your coaches and teammates help you stay consistent when motivation dips.' },
  { icon: TrendingUp, title: 'Progress You Can Measure', desc: 'Assessments and body composition tracking help you see real change beyond the scale.' },
  { icon: Layers, title: 'Options for Every Starting Point', desc: 'From personal training to large group coaching to official HYROX training.' },
];

const reviews = [
  { name: 'Tiffany Brown', text: "X-Team isn't your average gym. It's more like a fitness community... This is the only place where I've been held accountable, been engaged and have actually gotten excited about working out.", photo: 'https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/ilmrhewq_tiffany-brown.jpg', position: 'left' },
  { name: 'Justin Anderson', text: "It's a great fit for people of all levels of fitness... You will make a great new group of friends that holds each other accountable.", photo: 'https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/xfwmzwod_justin-anderson.jpg', position: 'right' },
  { name: 'Karissa Arthur', text: "The variety and the ability to do both indoor and outdoor workouts mean you never get bored and can work around your schedule.", photo: 'https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/jsgbfnzw_karissa-arthur.png', position: 'left' },
  { name: 'Jac Terrio', text: "X-Team is an awesome community where you can get fit, have fun, and make friends all at once.", photo: 'https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/hjenmgvq_jaclyn-terrio.jpg', position: 'right' },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero Section - Video starts at 135px (below nav), height 800px, ends at 935px */}
      <section className="relative" style={{ height: '935px', backgroundColor: '#000' }} data-testid="hero-section" aria-label="Hero">
        {/* Video Background - positioned at 135px from top, 800px height */}
        <div className="absolute left-1/2 transform -translate-x-1/2" style={{ top: '135px', width: '1900px', maxWidth: '100%', height: '800px' }}>
          <video 
            autoPlay 
            muted 
            loop 
            playsInline
            className="w-full h-full"
            style={{ objectFit: 'contain', objectPosition: 'top center' }}
          >
            <source src="/x-team-video.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          
          {/* Hero Content - positioned at bottom of video area */}
          <div className="absolute left-0 right-0 bottom-0 z-20" style={{ paddingBottom: '40px' }}>
            <div className="container-custom">
              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <Button 
                    data-testid="hero-cta-primary"
                    className="w-full sm:w-auto bg-[#FF5A3C] hover:bg-[#E64526] text-white rounded-none font-nav text-xl uppercase tracking-widest px-10 py-6 shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Book A Free Intro
                  </Button>
                </a>
                <Link to="/schedule">
                  <Button 
                    variant="outline"
                    data-testid="hero-cta-secondary"
                    className="w-full sm:w-auto bg-transparent border-2 border-white text-white hover:bg-white hover:text-gray-900 rounded-none font-nav text-xl uppercase tracking-widest px-10 py-6 transition-all duration-300"
                  >
                    View Schedule
                  </Button>
                </Link>
              </div>
              
              {/* Trust badges - below buttons */}
              <div className="flex flex-wrap items-center gap-6 text-white">
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#FF5A3C]" />
                  All fitness levels welcome
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#FF5A3C]" />
                  Official HYROX Affiliate
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#FF5A3C]" />
                  Scott's Addition + Outdoor locations
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Orange Panel with Headline and Body Text */}
      <section className="bg-[#FF5A3C] pt-12 pb-16 relative z-10" data-testid="headline-section" aria-label="Headline">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center pt-4">
            {/* Badge moved here from hero */}
            <div className="flex justify-center mb-10" data-testid="adventure-badge-wrapper">
              <div className="inline-flex items-center gap-3 bg-white text-gray-900 px-6 py-3 shadow-lg relative z-20" data-testid="adventure-badge">
                <MapPin className="w-5 h-5 text-[#FF5A3C] flex-shrink-0" />
                <span className="font-nav uppercase tracking-wider text-sm font-semibold">Richmond's Only Indoor/Outdoor Fitness Adventure</span>
              </div>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading text-white mb-6 leading-tight tracking-[0.06em]">
              Get Stronger, Leaner, and More Consistent With Richmond's Most Unique Fitness Community
            </h1>
            <p className="text-xl text-white/90">
              Coach-led indoor and outdoor training, personal training, nutrition coaching, measurable progress tracking, and official HYROX training — all designed to help you feel better, perform better, and actually stick with it.
            </p>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="bg-[#F5F7FA] py-8" data-testid="social-proof-section" aria-label="Social proof">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center">
            <a 
              href="https://g.page/xteamfitness" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              data-testid="google-rating-link"
              aria-label="See our 4.9 star rating on Google"
            >
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#FF5A3C] text-[#FF5A3C]" />
                ))}
              </div>
              <span className="font-bold text-[#1A1A1A]">4.9 stars</span>
              <span className="text-gray-500">from 50+ reviews</span>
              <span className="text-[#FF5A3C] font-semibold underline underline-offset-2">on Google</span>
            </a>
            <span className="hidden md:block w-px h-6 bg-gray-300"></span>
            <p className="text-gray-600">
              Trusted by Richmond adults who want accountability, variety, expert coaching, and real results.
            </p>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="section-padding bg-white" data-testid="who-its-for-section" aria-label="Who it is for">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[#1A1A1A] mb-6">
              You Don't Need to Be in Shape Before You Start
            </h2>
              <p className="text-lg text-gray-600 mb-8">
                X-Team is built for adults who want:
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'to lose weight and build strength',
                  'more energy and consistency',
                  'accountability that keeps them showing up',
                  'expert coaching without the pressure of a traditional gym',
                  'a program that is challenging, supportive, and actually fun'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#FF5A3C] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-600 mb-8">
                Whether you are getting back into fitness, pushing to a new level, or training for something specific like HYROX, we will help you find the right fit.
              </p>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button 
                  data-testid="who-its-for-cta"
                  className="bg-[#FF5A3C] hover:bg-[#E64526] text-white rounded-none font-nav uppercase tracking-widest px-8 py-4 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  Book a Free Intro
                </Button>
              </a>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/u1zsrncp_ring-row.jpg" 
                alt="Ring row exercise at X-Team Fitness"
                className="shadow-xl w-full"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why X-Team Section */}
      <section className="section-padding bg-[#F5F7FA]" data-testid="why-xteam-section" aria-label="Why X-Team">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[#1A1A1A] mb-4">
              More Than a Gym Membership
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Why people choose X-Team
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {differentiators.map((item, i) => (
              <div 
                key={i} 
                className="bg-white p-8 shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-2"
                data-testid={`differentiator-card-${i}`}
              >
                <div className="w-14 h-14 bg-gray-100 flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-[#FF5A3C]" />
                </div>
                <h3 className="font-heading text-xl text-[#1A1A1A] mb-3 tracking-wide">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Member Photos Gallery */}
      <section className="py-16 bg-[#DCDFE3]" data-testid="member-gallery-section" aria-label="Member photos">
        <div className="flex flex-col md:flex-row justify-center items-center gap-6 md:gap-[130px] px-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/08idmz2y_ryan%20clennan.jpg"
            alt="X-Team member Ryan training with dumbbells"
            className="w-full max-w-[290px] h-[300px] object-cover rounded-2xl"
            loading="lazy"
          />
          <img 
            src="https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/rhyq173d_3%20girls.jpg"
            alt="X-Team members smiling after workout"
            className="w-full max-w-[290px] h-[300px] object-cover rounded-2xl"
            loading="lazy"
          />
          <img 
            src="https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/hp9t9464_rex.jpg"
            alt="X-Team member Rex with tire flip"
            className="w-full max-w-[290px] h-[300px] object-cover rounded-2xl"
            loading="lazy"
          />
        </div>
      </section>

      {/* Programs Overview */}
      <section className="section-padding bg-white" data-testid="programs-section" aria-label="Programs overview">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[#1A1A1A] mb-4">
              Coaching Options Built Around Your Goals
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the coaching style that fits you best
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {programs.map((program, i) => (
              <div 
                key={i} 
                className="bg-white p-8 border border-gray-200 shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-2"
                data-testid={`program-card-${i}`}
              >
                <div className="w-14 h-14 bg-gray-100 flex items-center justify-center mb-6">
                  <program.icon className="w-7 h-7 text-[#FF5A3C]" />
                </div>
                <h3 className="font-heading text-xl text-[#1A1A1A] mb-3 tracking-wide">{program.title}</h3>
                <p className="text-gray-600">{program.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <Link to="/programs">
              <Button 
                variant="outline"
                data-testid="explore-programs-btn"
                className="border-2 border-gray-400 hover:border-[#FF5A3C] hover:text-[#FF5A3C] rounded-none font-nav uppercase tracking-widest px-8 py-4 transition-all duration-300"
              >
                Explore Programs <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How to Get Started */}
      <section className="section-padding bg-[#F5F7FA]" data-testid="how-to-start-section" aria-label="How to get started">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[#1A1A1A] mb-4">
              Getting Started is Simple
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              { step: '1', title: 'Book a Free Intro', desc: 'Meet one-on-one with a coach to talk through your goals, experience, and schedule.' },
              { step: '2', title: 'Get a Personalized Recommendation', desc: 'We will help you choose the best place to begin, whether that is personal training, large group, HYROX, or another option.' },
              { step: '3', title: 'Start Training With Support', desc: 'Show up, get coached, and build momentum with a team behind you.' },
            ].map((item, i) => (
              <div key={i} className="text-center" data-testid={`step-${item.step}`}>
                <div className="w-16 h-16 bg-[#FF5A3C] text-white flex items-center justify-center font-heading text-3xl mx-auto mb-6">
                  {item.step}
                </div>
                <h3 className="font-heading text-2xl text-[#1A1A1A] mb-3 tracking-wide">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                data-testid="how-to-start-cta"
                className="bg-[#FF5A3C] hover:bg-[#E64526] text-white rounded-none font-nav uppercase tracking-widest px-8 py-4 shadow-md hover:shadow-lg transition-all duration-300"
              >
                Book a Free Intro
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Community Image */}
      <section className="py-0" data-testid="community-image-section" aria-label="Community">
        <img 
          src="https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/c135yffs_tpot-situps%201218.jpg"
          alt="X-Team community workout at outdoor event"
          className="w-full h-auto"
          loading="lazy"
        />
      </section>

      {/* Reviews Section */}
      <section className="section-padding bg-white" data-testid="reviews-section" aria-label="Member reviews">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[#1A1A1A] mb-4">
              What Members Say
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {reviews.map((review, i) => (
              <div 
                key={i} 
                className={`bg-white p-8 shadow-[0_4px_20px_rgba(0,0,0,0.05)] border border-gray-200 flex ${review.position === 'right' ? 'flex-row-reverse' : 'flex-row'} gap-6 items-start`}
                data-testid={`review-card-${i}`}
              >
                {/* Photo */}
                <div className="flex-shrink-0">
                  <img 
                    src={review.photo}
                    alt={review.name}
                    className="w-[100px] h-[100px] rounded-full object-cover border-2 border-[#FF5A3C]"
                    style={{ objectPosition: 'center 10%' }}
                    loading="lazy"
                  />
                </div>
                
                {/* Content */}
                <div className="flex-1">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#FF5A3C] text-[#FF5A3C]" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">"{review.text}"</p>
                  <p className="font-semibold text-[#1A1A1A]">— {review.name}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://g.page/xteamfitness/review" target="_blank" rel="noopener noreferrer">
              <Button 
                variant="outline"
                className="border-2 border-gray-400 hover:border-[#FF5A3C] hover:text-[#FF5A3C] rounded-none font-nav uppercase tracking-widest px-8 py-4 transition-all duration-300"
              >
                Read More Reviews
              </Button>
            </a>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                className="bg-[#FF5A3C] hover:bg-[#E64526] text-white rounded-none font-nav uppercase tracking-widest px-8 py-4 shadow-md hover:shadow-lg transition-all duration-300"
              >
                Book a Free Intro
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Schedule Preview */}
      <section className="section-padding bg-[#F5F7FA]" data-testid="schedule-preview-section" aria-label="Schedule preview">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[#1A1A1A] mb-6">
                Morning and Evening Options That Fit Your Life
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Train before work, after work, indoors, outdoors, or both.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Early morning classes',
                  'Evening classes',
                  'Indoor large group',
                  'Outdoor large group',
                  'Running sessions',
                  'Yoga / mobility options',
                  'HYROX training options'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[#FF5A3C]" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/schedule">
                <Button 
                  data-testid="schedule-preview-cta"
                  className="bg-[#FF5A3C] hover:bg-[#E64526] text-white rounded-none font-nav uppercase tracking-widest px-8 py-4 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  View Full Schedule <Calendar className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/45680n4d_belle-isle-sunrise.jpg"
                alt="Belle Isle sunrise workout with X-Team"
                className="shadow-xl w-full"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Richmond Advantage */}
      <section className="section-padding bg-white" data-testid="richmond-section" aria-label="Richmond advantage">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/jp59ntvx_belle-isle-rocks-sm.jpg" 
                alt="Richmond outdoor training at Belle Isle"
                className="shadow-xl w-full"
                loading="lazy"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[#1A1A1A] mb-6 tracking-wide">
                A Richmond Fitness Experience You Will Not Get Anywhere Else
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                X-Team combines expert coaching with the energy of the city itself.
              </p>
              <p className="text-gray-600 mb-6">
                Train indoors in Scott's Addition and outdoors at parks and locations across Richmond. Members love the variety, the scenery, and the way it keeps workouts fresh and motivating.
              </p>
              <p className="text-gray-700 font-semibold mb-8">
                This is not another treadmill-and-TV gym.<br />
                This is coached training with accountability, variety, community, and purpose.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-gradient-to-br from-[#FF5A3C] to-[#E64526] text-white" data-testid="final-cta-section" aria-label="Call to action">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading mb-6 tracking-wide">
            Ready to Stop Starting Over?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            If you want expert coaching, motivating workouts, measurable progress, and a team that helps you stay consistent, your first step is simple.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button 
              data-testid="final-cta-btn"
              className="bg-white text-[#FF5A3C] hover:bg-gray-100 rounded-none font-nav uppercase tracking-widest px-10 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Book a Free Intro
            </Button>
          </a>
          <p className="text-white/80 mt-6">
            Talk with a coach, learn your options, and find the right path forward.
          </p>
        </div>
      </section>
    </div>
  );
}
